import { Box, Button, ButtonGroup, ListItem, Text, Textarea, UnorderedList } from '@chakra-ui/react';
import { CustomFormControl, Modal, formatDate, parseDate } from 'shared-components';
import { IApplicantOfferWithUIState } from '@/store/apply/reducer';
import { IApplicantOfferRequirementTask } from 'types';
import { JSX, useState } from 'react';

interface IssueOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  actionButtonLabel: string;
  offer: IApplicantOfferWithUIState;
  programme: IApplicantOfferWithUIState['programme'];
  conditionReqs: IApplicantOfferRequirementTask[];
  onConfirm: (startText?: string, endText?: string) => void;
  isLoading: boolean;
  defaultStartText?: string;
  defaultEndText?: string;
}

export const IssueOfferModal = ({
  isOpen,
  onClose,
  actionButtonLabel,
  offer,
  programme,
  conditionReqs,
  onConfirm,
  isLoading,
  defaultStartText = '',
  defaultEndText = '',
}: IssueOfferModalProps): JSX.Element | null => {
  const [startText, setStartText] = useState(defaultStartText);
  const [endText, setEndText] = useState(defaultEndText);

  // Sync state when defaults change (e.g. modal re-opens with different offer)
  const [prevDefaults, setPrevDefaults] = useState({ startText: defaultStartText, endText: defaultEndText });
  if (defaultStartText !== prevDefaults.startText || defaultEndText !== prevDefaults.endText) {
    setStartText(defaultStartText);
    setEndText(defaultEndText);
    setPrevDefaults({ startText: defaultStartText, endText: defaultEndText });
  }

  const isConditional = conditionReqs.length > 0;
  const pendingProgramme = offer.pendingChanges?.programme;
  const activeProgramme = {
    ...programme,
    ...pendingProgramme,
  };

  const handleConfirm = (): void => {
    onConfirm(startText, endText);
  };

  return (
    <Modal
      show={isOpen}
      onClose={onClose}
      title={actionButtonLabel}
      footer={
        <ButtonGroup spacing={3} size={'lg'}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="solid" onClick={handleConfirm} isLoading={isLoading}>
            {!conditionReqs.length ? actionButtonLabel : 'Issue conditional offer'}
          </Button>
        </ButtonGroup>
      }
    >
      <Box mb={6}>
        {isConditional ? (
          <>
            <Text>
              The applicant will now receive any Conditions and Enrolment requests as tasks. Incomplete pre-offer tasks
              will be upgraded to conditions.
            </Text>
            <Text>
              If you want to issue an unconditional offer, please cancel this dialog and ensure that all requirements
              are satisfied or exempt.
            </Text>
          </>
        ) : (
          <Text>All requirements are satisfied or exempt so an unconditional offer will be issued.</Text>
        )}
      </Box>

      <CustomFormControl label="Offer letter opening">
        <Textarea
          value={startText}
          onChange={e => setStartText(e.target.value)}
          rows={4}
          data-testid="start-text-input"
        />
      </CustomFormControl>

      <Box mb={4} color="muted">
        <Text fontSize="sm" mb={2}>
          Full details of the course are appended to the letter as below:
        </Text>
        <UnorderedList fontSize="sm" spacing={1}>
          <ListItem>
            Course: <b>{activeProgramme?.courseName}</b>
          </ListItem>
          <ListItem>
            Mode: <b>{activeProgramme?.mode}</b>
          </ListItem>
          <ListItem>
            Entry level: <b>{activeProgramme?.entryPoint}</b>
          </ListItem>
          <ListItem>
            Start date:{' '}
            <b>{activeProgramme?.startDate ? formatDate(parseDate(activeProgramme.startDate), 'D MMM YYYY') : ''}</b>
          </ListItem>
          <ListItem>
            End date:{' '}
            <b>{activeProgramme?.endDate ? formatDate(parseDate(activeProgramme.endDate), 'D MMM YYYY') : ''}</b>
          </ListItem>
          <ListItem>
            Course fee: <b>&pound;{activeProgramme?.fee?.toLocaleString() ?? '0'}</b>
          </ListItem>
        </UnorderedList>
      </Box>

      <CustomFormControl label="Offer letter closing">
        <Textarea value={endText} onChange={e => setEndText(e.target.value)} rows={3} data-testid="end-text-input" />
      </CustomFormControl>
    </Modal>
  );
};
